import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";

import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import "./NumberDropLeaderboard.css";

function etParts(now = new Date()) {
  const bag = {};
  new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    weekday: "short",
    hourCycle: "h23",
  }).formatToParts(now).forEach((part) => {
    bag[part.type] = part.value;
  });
  return bag;
}

function weekStartKey(now = new Date()) {
  const parts = etParts(now);
  const utc = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
  const daysSinceMonday = (utc.getUTCDay() + 6) % 7;
  utc.setUTCDate(utc.getUTCDate() - daysSinceMonday);
  const month = String(utc.getUTCMonth() + 1).padStart(2, "0");
  const day = String(utc.getUTCDate()).padStart(2, "0");
  return `${utc.getUTCFullYear()}-${month}-${day}`;
}

function shiftDateKey(iso, days) {
  const [year, month, day] = String(iso).split("-").map(Number);
  const utc = new Date(Date.UTC(year, month - 1, day));
  utc.setUTCDate(utc.getUTCDate() + days);
  const nextMonth = String(utc.getUTCMonth() + 1).padStart(2, "0");
  const nextDay = String(utc.getUTCDate()).padStart(2, "0");
  return `${utc.getUTCFullYear()}-${nextMonth}-${nextDay}`;
}

function asNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function currentWeekBounds(now = new Date()) {
  const parts = etParts(now);
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(parts.weekday);
  const hour = Number(parts.hour);
  let start = weekStartKey(now);
  if (weekday === 1 && hour < 9) start = shiftDateKey(start, -7);
  const today = `${parts.year}-${parts.month}-${parts.day}`;
  return { start, end: today, today };
}

function daysUntilMondayReset(now = new Date()) {
  const parts = etParts(now);
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(parts.weekday);
  const hour = Number(parts.hour);
  if (weekday === 1 && hour < 9) return 0;
  const daysSinceMonday = (weekday + 6) % 7;
  const untilNext = daysSinceMonday === 0 ? 7 : 7 - daysSinceMonday;
  return untilNext;
}

function rankLiveScores(scoreDocs, { today }) {
  const totals = new Map();
  scoreDocs.forEach((data) => {
    const userId = data.userId;
    if (!userId) return;
    const prev = totals.get(userId) || {
      userId,
      points: 0,
      daysPlayed: 0,
      solvedGuesses: 0,
      solvedDate: "",
    };
    prev.points += asNumber(data.points);
    prev.daysPlayed += 1;
    if (data.solved === true) {
      const when = String(data.date || "");
      if (!prev.solvedDate || when >= prev.solvedDate) {
        prev.solvedDate = when;
        prev.solvedGuesses = asNumber(data.guessCount);
      }
    }
    if (data.date === today && data.solved === true) {
      prev.solvedGuesses = asNumber(data.guessCount);
      prev.solvedDate = today;
    }
    totals.set(userId, prev);
  });
  return [...totals.values()]
    .map((row) => ({
      userId: row.userId,
      points: row.points,
      daysPlayed: row.daysPlayed,
      solvedGuesses: row.solvedGuesses,
      displayName: "",
    }))
    .sort((a, b) => b.points - a.points || String(a.userId).localeCompare(String(b.userId)))
    .map((row, index) => ({ ...row, rank: index + 1 }));
}

function firstName(displayName) {
  const name = String(displayName || "").trim();
  if (!name) return "Player";
  return name.split(/\s+/)[0];
}

function shortName(displayName) {
  const parts = String(displayName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || "Player";
  return `${parts[0]} ${parts[parts.length - 1][0].replace(".", "")}.`;
}

function initials(displayName) {
  const parts = String(displayName || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "•";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function normalizeRow(row) {
  return {
    userId: row.userId,
    rank: asNumber(row.rank),
    points: asNumber(row.points),
    daysPlayed: asNumber(row.daysPlayed),
    solvedGuesses: asNumber(row.solvedGuesses),
    displayName: row.displayName || "",
  };
}

function neighbourhood(rows, viewerId, creatorId) {
  const sorted = [...rows].filter((row) => row.rank > 0).sort((a, b) => a.rank - b.rank);
  const picked = new Map();
  const add = (row) => {
    if (row?.userId) picked.set(row.rank, row);
  };
  sorted.filter((row) => row.rank <= 3).forEach(add);
  const creator = sorted.find((row) => row.userId === creatorId);
  if (creator && creator.rank > 3) add(creator);
  const viewer = sorted.find((row) => row.userId === viewerId);
  if (viewer) {
    add(sorted.find((row) => row.rank === viewer.rank - 1));
    add(viewer);
    add(sorted.find((row) => row.rank === viewer.rank + 1));
  }
  const list = [...picked.values()].sort((a, b) => a.rank - b.rank);
  const rendered = [];
  list.forEach((row, index) => {
    if (index > 0 && row.rank > list[index - 1].rank + 1) {
      rendered.push({ kind: "gap", key: `gap-${row.rank}` });
    }
    rendered.push({ kind: "row", key: row.userId, row });
  });
  return rendered;
}

function sumBoards(boards) {
  const totals = new Map();
  boards.forEach((board) => {
    (board.top || []).forEach((row) => {
      const prev = totals.get(row.userId) || {
        userId: row.userId,
        points: 0,
        daysPlayed: 0,
        solvedGuesses: 0,
        displayName: row.displayName || "",
      };
      prev.points += asNumber(row.points);
      prev.daysPlayed += asNumber(row.daysPlayed);
      if (!prev.displayName && row.displayName) prev.displayName = row.displayName;
      totals.set(row.userId, prev);
    });
  });
  return [...totals.values()]
    .sort((a, b) => b.points - a.points || String(a.userId).localeCompare(String(b.userId)))
    .map((row, index) => ({ ...row, rank: index + 1 }));
}

function digitLabel(grid) {
  return grid === 5 ? "5-digit" : "4-digit";
}

function metaFor(row, { isCreator, label }) {
  if (isCreator && row.solvedGuesses) return `Creator · solved in ${row.solvedGuesses}`;
  if (row.solvedGuesses) return `Solved in ${row.solvedGuesses} · ${label}`;
  if (row.daysPlayed) return `${row.daysPlayed} of 7 days`;
  return "";
}

function resetLine(resetDays) {
  const left = resetDays === 0 ? "today" : `${resetDays} ${resetDays === 1 ? "day" : "days"} left`;
  return `Resets Monday 9:00am ET · ${left}`;
}

export default function NumberDropLeaderboard() {
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const creatorId = (searchParams.get("creator") || "").trim();
  const grid = Number(searchParams.get("grid")) === 5 ? 5 : 4;
  const [range, setRange] = useState("week");
  const [creator, setCreator] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [liveWeek, setLiveWeek] = useState([]);
  const [names, setNames] = useState({});
  const [prize, setPrize] = useState({ weekly: "", allTime: "" });
  const [scoresError, setScoresError] = useState("");
  const [boardsError, setBoardsError] = useState("");
  const weekBounds = useMemo(() => currentWeekBounds(), []);

  useEffect(() => {
    let alive = true;
    getDoc(doc(db, "config", "wordDropPrizes"))
      .then((snap) => {
        if (!alive || !snap.exists()) return;
        const entry = snap.data()?.numberDrop || {};
        setPrize({
          weekly: entry.weekly || "",
          allTime: entry.allTime || "",
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    if (!creatorId) {
      setCreator(null);
      setWeekly([]);
      setLiveWeek([]);
      return undefined;
    }
    setScoresError("");
    setBoardsError("");
    setWeekly([]);
    setLiveWeek([]);
    (async () => {
      try {
        const profile = await getDoc(doc(db, "users", creatorId));
        if (!alive) return;
        setCreator({
          id: creatorId,
          displayName: profile.exists() ? (profile.data()?.displayName || "Creator") : "Creator",
        });
        const [boardsResult, scoresResult] = await Promise.allSettled([
          getDocs(query(
            collection(db, "numberDrop_weeklyBoards"),
            where("creatorId", "==", creatorId)
          )),
          getDocs(query(
            collection(db, "numberDrop_scores"),
            where("creatorId", "==", creatorId),
            where("grid", "==", grid),
            where("date", ">=", weekBounds.start),
            where("date", "<=", weekBounds.end)
          )),
        ]);
        if (!alive) return;
        if (boardsResult.status === "fulfilled") {
          setWeekly(boardsResult.value.docs
            .map((snap) => ({ id: snap.id, ...snap.data() }))
            .filter((board) => Number(board.grid) === grid));
        }
        if (scoresResult.status === "fulfilled") {
          setLiveWeek(rankLiveScores(
            scoresResult.value.docs.map((snap) => snap.data() || {}),
            { today: weekBounds.today }
          ).map(normalizeRow));
        }
        if (boardsResult.status === "rejected") {
          setBoardsError(boardsResult.reason?.message || "This board couldn't be loaded.");
        }
        if (scoresResult.status === "rejected") {
          setScoresError(scoresResult.reason?.message || "This board couldn't be loaded.");
        }
      } catch (err) {
        if (!alive) return;
        setWeekly([]);
        setLiveWeek([]);
        const message = err?.message || "This board couldn't be loaded.";
        setScoresError(message);
        setBoardsError(message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [creatorId, grid, weekBounds]);

  const rows = useMemo(() => {
    if (range === "week") return liveWeek;
    return sumBoards(weekly.map((board) => ({
      ...board,
      top: (board.top || []).map(normalizeRow),
    })));
  }, [range, liveWeek, weekly]);

  useEffect(() => {
    const missing = [...new Set(rows.map((row) => row.userId).filter((id) => id && !Object.prototype.hasOwnProperty.call(names, id)))];
    if (!missing.length) return undefined;
    let alive = true;
    (async () => {
      const loaded = {};
      await Promise.all(missing.map(async (id) => {
        const snap = await getDoc(doc(db, "users", id));
        loaded[id] = snap.exists() ? (snap.data()?.displayName || "") : "";
      }));
      if (alive) setNames((prev) => ({ ...prev, ...loaded }));
    })();
    return () => {
      alive = false;
    };
  }, [rows, names]);

  const namedRows = rows.map((row) => ({
    ...row,
    displayName: shortName(names[row.userId] || row.displayName || "Player"),
  }));
  const visible = neighbourhood(namedRows, currentUser?.uid || "", creatorId);
  const resetDays = daysUntilMondayReset();
  const label = digitLabel(grid);
  const leagueName = firstName(creator?.displayName);
  const calloutTitle = (range === "week" ? prize.weekly : prize.allTime) || "Prize details are coming.";
  const error = range === "week" ? scoresError : boardsError;
  const gameHref = `/games?creator=${encodeURIComponent(creatorId)}&grid=${grid}`;

  function selectGrid(next) {
    const params = new URLSearchParams(searchParams);
    params.set("grid", String(next));
    if (creatorId) params.set("creator", creatorId);
    setSearchParams(params, { replace: true });
  }

  return (
    <div className="ndl-page">
      <header className="ndl-header">
        <Link className="ndl-back" to={gameHref}>← Back</Link>
        <div className="ndl-kicker">NUMBER DROP · BOARDS</div>
        <div className="ndl-header-spacer" />
      </header>

      <div className="ndl-hero">
        <div className="ndl-hero-avatar" aria-hidden="true">{initials(creator?.displayName)}</div>
        <div className="ndl-hero-copy">
          <h1 className="ndl-title">{leagueName}'s {label} league</h1>
          <div className="ndl-sub">
            {range === "week" ? resetLine(resetDays) : "All-time points on this board"}
          </div>
        </div>
      </div>

      <div className="ndl-segs" role="tablist" aria-label="Number length">
        {[4, 5].map((size) => (
          <button
            key={size}
            type="button"
            role="tab"
            className={`ndl-tab${grid === size ? " active" : ""}`}
            aria-selected={grid === size}
            onClick={() => selectGrid(size)}
          >
            {digitLabel(size)}
          </button>
        ))}
      </div>

      <div className="ndl-segs range" role="tablist" aria-label="Range">
        <button type="button" role="tab" className={`ndl-tab${range === "week" ? " active" : ""}`} aria-selected={range === "week"} onClick={() => setRange("week")}>This week</button>
        <button type="button" role="tab" className={`ndl-tab${range === "all" ? " active" : ""}`} aria-selected={range === "all"} onClick={() => setRange("all")}>All time</button>
      </div>

      <div className="ndl-callout">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7A4B03" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M8 21h8M12 17v4M7 4h10l-1 7a4 4 0 0 1-8 0L7 4Z" />
          <path d="M5 4h2v4a3 3 0 0 1-3-3c0-.6.4-1 1-1ZM19 4h-2v4a3 3 0 0 0 3-3c0-.6-.4-1-1-1Z" />
        </svg>
        <div className="ndl-callout-copy">
          <div className="ndl-callout-title">{calloutTitle}</div>
          {range === "week" && <div className="ndl-callout-sub">{resetLine(resetDays)}</div>}
        </div>
      </div>

      <div className="ndl-list">
        {error && <div className="ndl-empty">{error}</div>}
        {!error && visible.length === 0 && <div className="ndl-empty">No scores on this board yet.</div>}
        {visible.map((item) => {
          if (item.kind === "gap") return <div className="ndl-gap" key={item.key}>···</div>;
          const row = item.row;
          const isYou = Boolean(currentUser?.uid) && row.userId === currentUser.uid;
          const isCreator = row.userId === creatorId;
          const meta = metaFor(row, { isCreator, label });
          return (
            <div
              className={`ndl-row${isYou ? " you" : ""}${isCreator ? " creator" : ""}`}
              key={item.key}
            >
              <div className={`ndl-rank${row.rank <= 3 ? " top" : ""}`}>{row.rank}</div>
              <div className={`ndl-avatar${isCreator ? " creator" : ""}`} aria-hidden="true">
                {initials(names[row.userId] || row.displayName)}
              </div>
              <div className="ndl-main">
                <div className="ndl-name">{isYou ? "You" : row.displayName}</div>
                {meta && <div className="ndl-meta">{meta}</div>}
              </div>
              <div className="ndl-pts">{row.points}</div>
            </div>
          );
        })}
      </div>

      <div className="ndl-footer">
        <Link className="ndl-play" to={gameHref}>Play today's numbers</Link>
        <div className="ndl-foot">Only {leagueName}'s subscribers and followers are on this board</div>
      </div>
    </div>
  );
}
